import { lazy, Suspense, useEffect, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { BrowserRouter, Route, Routes } from 'react-router-dom'

import { GranicaGreske } from './app/GranicaGreske'
import { Korijen } from './app/Korijen'
import { jeBezSesije, jeGostovaAdresa } from './app/rute'
import { NedostupnoSucelje } from './app/Zasticeno'
import { obnoviSesiju } from './auth/session'
import { useSession } from './auth/useSession'
import { ChangePasswordPage } from './pages/ChangePasswordPage'
import { LoginPage } from './pages/LoginPage'
import { UpitOdgovor } from './pages/UpitOdgovor'
import { NovoIzdanje } from './pwa/NovoIzdanje'

/**
 * Sučelja u jednoj bazi koda (CLAUDE.md §13), ali ne i u jednom paketu: gost,
 * vozač i konzola (recepcija + admin + platforma) učitavaju se zasebno. Gost na
 * hotelskom wifiju ne treba šifrarnike resorta, a vozač na mobitelu ne treba
 * cjenik — mobile-first je i mjera koliko se preuzima, ne samo kako izgleda.
 */
const GostAplikacija = lazy(() => import('./gost/aplikacija'))
const VozacAplikacija = lazy(() => import('./vozac/aplikacija'))
const Konzola = lazy(() => import('./app/konzola'))

/**
 * ONBOARDING UPITNIK (O1 §2) — vlastiti chunk, obrazloženje uz samu stranicu.
 */
const Postavljanje = lazy(() => import('./pages/Postavljanje'))

/**
 * NACRT NASLOVNICE (L1P) — vlastiti chunk iz istog razloga kao upitnik: jedan
 * ekran s vlastitim fontom i dvije fotografije, koji se otvara desetak puta i
 * ne smije ga plaćati svatko tko aplikaciju ikad podigne.
 */
const LivingPreview = lazy(() => import('./pages/LivingPreview'))

function Ucitavanje() {
  const { t } = useTranslation()

  return (
    <div role="status" className="flex min-h-dvh items-center justify-center bg-noir text-stone">
      {t('state.loading')}
    </div>
  )
}

export default function App() {
  const sesija = useSession()

  // Na kojoj je površini aplikacija pokrenuta. Čita se JEDNOM, iz adrese s kojom
  // je stranica otvorena: gost i osoblje se ovdje razdvajaju, a ne kasnije.
  // Gostova adresa i javna stranica upita (Z22c §A) dijele isti ishod: sesija
  // OSOBLJA se ne dira. Razlog je isti — ondje ona nikome ne treba, a podizanje
  // bi rotiralo tuđi refresh token.
  const [gostovaAdresa] = useState(() => {
    const putanja = globalThis.location?.pathname ?? ''

    return jeGostovaAdresa(putanja) || jeBezSesije(putanja)
  })

  // Access token živi samo u memoriji (§13), pa se sesija pri svakom pokretanju
  // podiže iz refresh tokena — što je usput i trenutak u kojem poslužitelj kaže
  // vrijedi li još (ugašen resort, deaktiviran račun). Sesiju gosta podiže
  // njegov okvir: ekrani osoblja je ne trebaju.
  //
  // Na adresi gosta se sesija osoblja NE dira. Nije njegova, ne treba mu ni za
  // jedan ekran, a podizanje bi rotiralo tuđi refresh token pri svakom otvaranju
  // aplikacije (zadatak 10 §1, vidi `app/rute.ts`).
  useEffect(() => {
    if (!gostovaAdresa) void obnoviSesiju()
  }, [gostovaAdresa])

  // Iz istog razloga gost ne čeka iza tuđeg osvježavanja: vrtuljak dok se podiže
  // sesija ima smisla samo ondje gdje o toj sesiji ovisi što će se prikazati.
  if (!gostovaAdresa && sesija.ucitavanje) {
    return <Ucitavanje />
  }

  return (
    <BrowserRouter>
      {/*
        Granica stoji IZNAD `Suspense`a: odbijeno obećanje iz `lazy()` ne pada u
        `fallback` nego uz stablo prema gore, pa bi ga niža granica promašila.
      */}
      <GranicaGreske>
        <NovoIzdanje />
        <Suspense fallback={<Ucitavanje />}>
          <Routes>
            {/*
              Korijen je adresa instalacije (`manifest.webmanifest`:
              `start_url: "/"`) i jedina koja ne pripada nijednoj ulozi — pa je
              sam odlučuje (`app/Korijen.tsx`).
            */}
            <Route path="/" element={<Korijen />} />

            <Route path="/g/*" element={<GostAplikacija />} />

            <Route path="/login" element={<LoginPage />} />
            <Route path="/change-password" element={<ChangePasswordPage />} />
            <Route path="/nedostupno" element={<NedostupnoSucelje />} />

            {/*
              ODGOVOR NA UPIT IZ E-POŠTE (Z22c §A) — javna stranica bez prijave,
              za pružatelja koji Shuttly nema. Kratka adresa (`/u`), a ne pod
              `/g`: ovo nije gostova aplikacija nego jedan ekran, i tako izgleda
              i u sandučiću. Nije lazy chunk — otvara se točno jednom, iz poruke,
              a odgoda učitavanja tu ne štedi ništa nikome.
            */}
            <Route path="/u" element={<UpitOdgovor />} />

            {/*
              ONBOARDING UPITNIK (O1 §2) — javni obrazac kojim klijent postavlja
              svoj resort, prije nego što resort postoji. Token je u PUTANJI, a
              ne u upitu: adresa se otvara desecima puta kroz nekoliko tjedana,
              bookmarka se i prosljeđuje kolegi, pa mora izgledati kao adresa.
            */}
            <Route path="/postavljanje/:token" element={<Postavljanje />} />

            {/*
              NACRT NASLOVNICE "LIVING STAY" (L1P) — preview za presudu, bez
              prijave i bez ijedne poveznice iz navigacije. Stoji ovdje, a ne
              pod `/g`: gostova aplikacija je ono što POSTOJI, a ovo je prijedlog
              onoga što bi mogla biti, pa se ne smije zateći ni u njezinoj
              ljusci ni u njezinu pojasu.
            */}
            <Route path="/living-preview" element={<LivingPreview />} />

            <Route path="/vozac/*" element={<VozacAplikacija />} />

            {/*
              KONZOLA ZAPOSLENIKA — sve ostalo (NAV-LJUSKA).

              Do ovog zahvata je ovdje stajalo šest prefiksa (`/sys`, `/admin`,
              `/reception`, `/uvid`, `/kampanje`, `/ponude`) plus sedmi za laku
              površinu, a svaki je konzoli morao reći koji je dio adrese potrošen:
              `<Routes>` unutar montirane komponente vidi SAMO ostatak adrese
              (zadatak 11, §17). Sada je montaža jedna, na `path="*"`, pa konzola
              vidi cijelu adresu i drži cijelu svoju kartu na jednom mjestu — što
              je i uvjet da izbornik bude jedan.

              Nepoznata adresa i dalje završava na KORIJENU; taj račun stoji u
              samoj konzoli (`app/konzola.tsx`), jer sve što nije gost, vozač ni
              javna stranica prolazi kroz nju. Anoniman posjetitelj tako ne
              završava na prijavi osoblja — za gosta bi to bio zid, jer lozinku
              nema ni nema gdje dobiti.
            */}
            <Route path="*" element={<Konzola />} />
          </Routes>
        </Suspense>
      </GranicaGreske>
    </BrowserRouter>
  )
}
